// Datos de la ficha de crédito. CINCO lecturas independientes: cada bloque falla y reintenta por
// su cuenta, así que un 403 en `loan.*` no puede tumbar la pestaña Dispositivo, que sí responde.
import { useQuery } from '@tanstack/react-query';
import { CREDIT_ID_PATTERN } from '@fiado/type-kit/bin/common/constants/CreditIdPattern';
import type { LoanContractResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanContractResponse';
import type { LoanInstallmentResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanInstallmentResponse';
import { ApiError } from '@/api/errors';
import {
    loanCreditApi,
    mdmBackofficeApi,
    type LoanCreditDetail,
    type LoanDeviceFleetItem,
    type LoanDeviceOperationItem,
} from '@/api/services/loan-credit';
import { CREDIT_FILE_DEVICE_KEY, MDM_OPERATIONS_KEY } from '../../mdm/api/queryKeys';
import { classifyFailure, type CreditFileFailure } from '../failures';

const CREDIT_KEY = ['credit-file', 'credit'] as const;
const INSTALLMENTS_KEY = ['credit-file', 'installments'] as const;
const CONTRACTS_KEY = ['credit-file', 'contracts'] as const;

const STALE_TIME_MS = 30_000;

/** El resultado de UN bloque: dato, si está en vuelo, por qué falló y cómo reintentarlo. */
export interface CreditFileBlock<T> {
    data: T | undefined;
    isLoading: boolean;
    /** `null` = no falló. Separa permiso, inexistente y falla de lectura. */
    failure: CreditFileFailure | null;
    retry: () => void;
}

export interface CreditFileData {
    credit: CreditFileBlock<LoanCreditDetail>;
    installments: CreditFileBlock<LoanInstallmentResponse[]>;
    contracts: CreditFileBlock<LoanContractResponse[]>;
    /** El equipo de ESTE crédito: `null` = no enrolado; `undefined` = sin leer o desconocido. */
    device: CreditFileBlock<LoanDeviceFleetItem | null>;
    operations: CreditFileBlock<LoanDeviceOperationItem[]>;
}

/** El expediente MDM del crédito; `null` sin equipo o si el id no es ULID (no llega a MDM). */
export async function fetchCreditDevice(creditId: string): Promise<LoanDeviceFleetItem | null> {
    if (!CREDIT_ID_PATTERN.test(creditId)) return null;
    try {
        return await mdmBackofficeApi.getMdmDevice(creditId);
    } catch (error) {
        if (error instanceof ApiError && error.code === 'MDM_DEVICE_NOT_FOUND') return null;
        throw error;
    }
}

/** La bitácora MDM del crédito; vacía si el id no es ULID. Misma forma que lee Control MDM. */
export function fetchCreditDeviceOperations(
    creditId: string,
): ReturnType<typeof mdmBackofficeApi.listMdmDeviceOperations> {
    if (!CREDIT_ID_PATTERN.test(creditId)) return Promise.resolve({ items: [] });
    return mdmBackofficeApi.listMdmDeviceOperations(creditId);
}

export function useCreditFile(creditId: string): CreditFileData {
    const credit = useQuery({
        queryKey: [...CREDIT_KEY, creditId],
        queryFn: () => loanCreditApi.getCredit(creditId),
        staleTime: STALE_TIME_MS,
        retry: false,
    });

    const installments = useQuery({
        queryKey: [...INSTALLMENTS_KEY, creditId],
        queryFn: () => loanCreditApi.listCreditInstallments(creditId),
        staleTime: STALE_TIME_MS,
        retry: false,
    });

    const contracts = useQuery({
        queryKey: [...CONTRACTS_KEY, creditId],
        queryFn: () => loanCreditApi.listCreditContracts(creditId),
        staleTime: STALE_TIME_MS,
        retry: false,
    });

    const device = useQuery({
        queryKey: [...CREDIT_FILE_DEVICE_KEY, creditId],
        queryFn: () => fetchCreditDevice(creditId),
        staleTime: STALE_TIME_MS,
        retry: false,
    });

    const operations = useQuery({
        queryKey: [...MDM_OPERATIONS_KEY, creditId],
        queryFn: () => fetchCreditDeviceOperations(creditId),
        staleTime: STALE_TIME_MS,
        retry: false,
    });

    // Sin dispositivo no hay bitácora que leer: su error no se le enseña al operador.
    const withoutDevice = device.data === null;

    return {
        credit: {
            data: credit.data,
            isLoading: credit.isLoading,
            failure: classifyFailure(credit.error),
            retry: () => {
                void credit.refetch();
            },
        },
        installments: {
            data: installments.data,
            isLoading: installments.isLoading,
            failure: classifyFailure(installments.error),
            retry: () => {
                void installments.refetch();
            },
        },
        contracts: {
            data: contracts.data,
            isLoading: contracts.isLoading,
            failure: classifyFailure(contracts.error),
            retry: () => {
                void contracts.refetch();
            },
        },
        device: {
            data: device.data,
            isLoading: device.isLoading,
            failure: classifyFailure(device.error),
            retry: () => {
                void device.refetch();
            },
        },
        operations: {
            data: withoutDevice && operations.error ? [] : operations.data?.items,
            isLoading: operations.isLoading,
            failure: withoutDevice ? null : classifyFailure(operations.error),
            retry: () => {
                void operations.refetch();
            },
        },
    };
}
